import React from 'react';
import {
  FlexRow,
  IconButton,
  FlexSpacer,
  Dropdown,
  DropdownMenuButton,
  Panel,
  DropMarker,
} from '@epam/promo';
import { cx, uuiDndState } from '@epam/uui-core';
import { DragHandle } from '@epam/uui-components';
import { ReactComponent as navigationMoreIcon } from '@epam/assets/icons/common/navigation-more_horiz-18.svg';
import { ReactComponent as DeleteIcon } from '@epam/assets/icons/common/action-delete-12.svg';

import { useTaskStore } from 'store/task';
import { useFavoritesStore } from 'store/favorites';
import { StatusIndicator } from 'components/common';
import { useDropdown, useFavouriteTaskToggle } from 'hooks';
import { useUserRole } from 'hooks/helpers';
import { CardProps } from './Card.model';
import css from './Card.module.scss';
import { FormattedDate } from '../../common/FormattedDate';
import { HighlightedTitle } from '../../common/HighlightedTitle';
import { CardDescription } from '../../common/CardDescription';
import { AllStatuses } from 'interfaces';
import { FavoriteStarToggle } from 'components/common/FavoriteStarToggle/FavoriteStarToggle';

export const Card = ({
  item,
  isActive,
  dndParams,
  isDragable,
  onClick,
  onDelete,
}: CardProps) => {
  const { openDropdownMenu } = useDropdown();
  const { isSuperAdmin } = useUserRole();
  const draftTask = useTaskStore((state) => state.draft);
  const favoritesUserId = useFavoritesStore((state) => state.userId);
  const isTaskFavorite = useFavoritesStore(
    (state) => !!state.favoriteTaskIds[item.id],
  );
  const { mutate: toggleFavouriteTask, isPending: isFavouritePending } =
    useFavouriteTaskToggle();

  return (
    <div
      ref={dndParams?.ref}
      {...dndParams?.eventHandlers}
      className={cx(css.dragElement, dndParams?.classNames)}>
      <div
        aria-hidden="true"
        className={cx(
          css.dndItem,
          {
            [uuiDndState.dragGhost]: dndParams?.isDragGhost,
          },
          isActive ? css['card__active-container'] : css.card__inactive,
        )}
        id={item.id}
        role="button"
        tabIndex={0}
        onClick={onClick}>
        <li
          className={cx(
            css.card,
            isSuperAdmin && isDragable && css.dragElement,
            isActive && css.card__active,
          )}>
          {isSuperAdmin &&
            draftTask.status !== AllStatuses.NOT_SAVED &&
            isDragable && <DragHandle cx={[css.dragHandle]} />}
          <FlexRow size={null}>
            <HighlightedTitle title={item.title} />
            <FlexSpacer />
            {favoritesUserId && item.status !== AllStatuses.NOT_SAVED && (
              <div className={css.favourite__star}>
                <FavoriteStarToggle
                  ariaLabel={`Toggle favourite for task "${item.title}"`}
                  data-testid="task-favourite-star"
                  disabled={isFavouritePending}
                  isActive={isTaskFavorite}
                  onToggle={() =>
                    toggleFavouriteTask({
                      taskId: item.id,
                      favorite: !isTaskFavorite,
                    })
                  }
                />
              </div>
            )}
            {isSuperAdmin && (
              <Dropdown
                placement="bottom-end"
                renderBody={() => (
                  <Panel shadow background="white">
                    <DropdownMenuButton
                      caption="Delete"
                      cx={css['dropdown-button']}
                      icon={DeleteIcon}
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        event.stopPropagation();
                        const target = event.target as HTMLInputElement;

                        onDelete(target.value);
                      }}
                    />
                  </Panel>
                )}
                renderTarget={({ ref, onClick: handleMenuItemClick }) => (
                  <IconButton
                    ref={ref}
                    icon={navigationMoreIcon}
                    onClick={(event: React.MouseEvent<HTMLElement>) =>
                      openDropdownMenu(event, handleMenuItemClick)
                    }
                  />
                )}
              />
            )}
          </FlexRow>
          <CardDescription description={item.description} />
          <FlexRow cx={css.card__details} size={null}>
            <StatusIndicator size="8" status={item.status} />
            <FlexSpacer />
            <FormattedDate date={item.updatedAt} />
          </FlexRow>
        </li>
        {dndParams && <DropMarker {...dndParams} />}
      </div>
    </div>
  );
};
